import { useState, useEffect, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";
import {
  Table,
  Tag,
  Typography,
  Spin,
  Button,
  Modal,
  Input,
  Space,
  Row,
  Col,
  Select
} from "antd";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

export default function PipApprovals() {
  const { authData, backUrl } = useContext(AuthContext);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // ⬇️ Added filter states
  const [searchName, setSearchName] = useState("");
  const [searchCreatedBy, setSearchCreatedBy] = useState("");
  const [searchStatus, setSearchStatus] = useState("");
  const [searchOverallStatus, setSearchOverallStatus] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [actionType, setActionType] = useState("");
  const [remarks, setRemarks] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${backUrl}/api/piprequest/pip-approval-tasks/`,
        {
          headers: {
            Authorization: `Bearer ${authData?.access}`,
          },
        }
      );
      setData(res.data.results || []);
      setFilteredData(res.data.results || []);
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Failed to load PIP approvals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ⬇️ Apply filtering logic whenever filters or data change
  useEffect(() => {
    let filtered = [...data];

    if (searchName) {
      filtered = filtered.filter((item) =>
        item.pip_request?.employee_name
          ?.toLowerCase()
          .includes(searchName.toLowerCase())
      );
    }

    if (searchCreatedBy) {
      filtered = filtered.filter((item) =>
        item.pip_request?.created_by_name
          ?.toLowerCase()
          .includes(searchCreatedBy.toLowerCase())
      );
    }

    if (searchStatus) {
      filtered = filtered.filter((item) => item.status === searchStatus);
    }

    if (searchOverallStatus) {
      filtered = filtered.filter(
        (item) => item.pip_request?.overall_status === searchOverallStatus
      );
    }

    setFilteredData(filtered);
  }, [data, searchName, searchCreatedBy, searchStatus, searchOverallStatus]);

  // Date formatter
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Open approve/reject modal
  const handleOpenModal = (record, action) => {
    setSelectedRecord(record);
    setActionType(action);
    setRemarks("");
    setIsModalOpen(true);
  };

  // Submit decision
  const handleSubmitDecision = async () => {
    if (!remarks.trim()) {
      toast.error("Remarks are required!");
      return;
    }

    setSubmitLoading(true);

    try {
      const url = `${backUrl}/api/piprequest/pip-approval-tasks/${selectedRecord.id}/submit-decision/`;

      await axios.post(
        url,
        { action: actionType, remarks: remarks.trim() },
        {
          headers: {
            Authorization: `Bearer ${authData?.access}`,
          },
        }
      );

      toast.success(`PIP ${actionType} successfully!`);
      setIsModalOpen(false);
      setRemarks("");
      fetchData();
    } catch (err) {
      console.error("Decision error:", err);
      toast.error("Failed to submit decision");
    } finally {
      setSubmitLoading(false);
    }
  };

  // Sub-table columns
  const approvalsColumns = [
    {
      title: "Order",
      dataIndex: "order",
      key: "order",
      width: 80,
    },
    {
      title: "Approver Name",
      dataIndex: "approver_name",
      key: "approver_name",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => (
        <Tag
          color={
            value === "Approved"
              ? "green"
              : value === "Rejected"
                ? "red"
                : "orange"
          }
        >
          {value}
        </Tag>
      ),
    },
    {
      title: "Completed On",
      dataIndex: "completed_on",
      key: "completed_on",
      render: (value) => formatDate(value),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
    },
  ];

  // Main columns
  const columns = [
    {
      title: "Employee Name",
      dataIndex: ["pip_request", "employee_name"],
      key: "employee_name",
    },
    {
      title: "Created By",
      dataIndex: ["pip_request", "created_by_name"],
      key: "created_by_name",
    },
    {
      title: "PIP Days",
      dataIndex: ["pip_request", "pip_days"],
      key: "pip_days",
    },
    {
      title: "Reason",
      dataIndex: ["pip_request", "reason"],
      key: "reason",
      render: (text) =>
        text?.length > 40 ? text.slice(0, 40) + "..." : text,
    },
    {
      title: "Your Remarks",
      dataIndex: "remarks",
      key: "remarks",
      render: (text) =>
        text?.length > 40 ? text.slice(0, 40) + "..." : text,
    },
    {
      title: "Your Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "Pending" ? "orange" : "green"}>
          {status}
        </Tag>
      ),
    },
    {
      title: "Overall Status",
      dataIndex: ["pip_request", "overall_status"],
      key: "overall_status",
      render: (status) => (
        <Tag color={status === "Pending" ? "orange" : "green"}>
          {status}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) =>
        record.status === "Pending" ? (
          <Space>
            <Button
              type="primary"
              onClick={() => handleOpenModal(record, "Approved")}
              style={{
                background: '#0e497b',
                color: 'white'
              }}
            >
              Approve
            </Button>
            <Button
              danger
              onClick={() => handleOpenModal(record, "Rejected")}
            >
              Reject
            </Button>
          </Space>
        ) : (
          <Tag color="green">Completed</Tag>
        ),
    },
  ];

  return (
    <>
      <ToastContainer />

      <Title level={4} style={{ marginBottom: 16 }}>
        PIP Approvals
      </Title>

      {/* ⬇️ FILTER SECTION */}
      <Row gutter={[10, 10]} style={{ marginBottom: 15 }}>
        <Col xs={24} sm={12} md={6}>
          <Input
            placeholder="Search Employee Name"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
          />
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Input
            placeholder="Search Created By"
            value={searchCreatedBy}
            onChange={(e) => setSearchCreatedBy(e.target.value)}
          />
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Select
            style={{ width: "100%" }}
            placeholder="Select Your Status"
            value={searchStatus || undefined}
            allowClear
            onChange={(v) => setSearchStatus(v)}
          >
            <Option value="Pending">Pending</Option>
            <Option value="Approved">Approved</Option>
            <Option value="Rejected">Rejected</Option>
          </Select>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Select
            style={{ width: "100%" }}
            placeholder="Select Overall Status"
            value={searchOverallStatus || undefined}
            allowClear
            onChange={(v) => setSearchOverallStatus(v)}
          >
            <Option value="Pending">Pending</Option>
            <Option value="Approved">Approved</Option>
            <Option value="Rejected">Rejected</Option>
          </Select>
        </Col>
      </Row>

      {loading ? (
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={filteredData}
          bordered
          pagination={{ pageSize: 5 }}
          expandable={{
            expandedRowRender: (record) => {
              const approvalsList =
                record.approvals ||
                record.pip_request?.approvals ||
                [];

              return (
                <Table
                  rowKey="id"
                  columns={approvalsColumns}
                  dataSource={approvalsList}
                  pagination={false}
                  size="small"
                  bordered
                />
              );
            },
          }}
          components={{
            header: {
              cell: (props) => (
                <th
                  {...props}
                  style={{
                    backgroundColor: "#0e497b",
                    color: "white",
                  }}
                >
                  {props.children}
                </th>
              ),
            },
          }}
        />
      )}

      {/* Modal */}
      <Modal
        title={`Submit ${actionType}`}
        open={isModalOpen}
        centered
        onCancel={() => setIsModalOpen(false)}
        onOk={handleSubmitDecision}
        okText="Submit"
        confirmLoading={submitLoading}
        cancelButtonProps={{ disabled: submitLoading }}
      >
        <p>
          Are you sure you want to <b>{actionType}</b> this PIP for{" "}
          <b>{selectedRecord?.pip_request?.employee_name}</b>?
        </p>

        <TextArea
          rows={4}
          placeholder="Enter remarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          disabled={submitLoading}
        />
      </Modal>
    </>
  );
}